// ══════════════ ENCADREMENT RENFORCÉ — guidage journée complète ══════════════
// Couche d'accompagnement "toute la journée" pour un salarié qui a besoin d'un
// cadre serré (blocages, rappels, clôture attestée). Complémentaire du mode guidé
// formation (ModeGuide.jsx), qui lui guide DANS l'appartement (le ménage).
//
// Activée par salarié via le champ staff `encadrementRenforce` (fiche SecRH).
// Les messages sont bilingues FR/ES selon le champ `langue` du salarié — la
// personne visée ici est hispanophone et lit mal le français. Ce n'est PAS un
// système i18n global : on scope volontairement les chaînes à cette couche.
//
// Lot A (v112) : AssistanceButton — un tap prévient la Direction + propose l'appel.
// Les lots suivants (blocage début appart sans début journée, écran de clôture,
// détection de trou entre apparts) viendront se greffer ici.
import React, { useState } from 'react';
import { notifEventBus } from '../notifBus.js';
import { distM, pausesDansFenetre } from '../utils.js';

// Helper bilingue scopé : renvoie la chaîne dans la langue du salarié, repli FR
// systématique (jamais d'écran vide sur une langue non remplie).
export const tr=(langue,m)=>(langue==='es'?(m.es??m.fr):m.fr);

// Motifs pré-remplis, bilingues — évitent au salarié d'écrire en français.
// Le libellé `fr` est celui transmis à la Direction (qui lit le français).
const MOTIFS=[
  {id:'rapport',fr:'Le rapport ne marche pas',es:'El informe no funciona'},
  {id:'introuvable',fr:'Je ne trouve pas quelque chose',es:'No encuentro algo'},
  {id:'autre',fr:'Autre',es:'Otro'},
];

// Bouton d'assistance terrain. Affiché uniquement pour un salarié en encadrement
// renforcé (le rendu conditionnel est fait par l'appelant, StaffUI). Un tap →
// choix du motif → prévient la Direction (notif `assistance_demandee`, en FR) +
// propose l'appel direct aux gérants joignables. Fire-and-forget : ça n'échoue
// jamais du point de vue du salarié.
function AssistanceButton({me,gerants,onAssist}){
  const langue=me?.langue||'fr';
  const[open,setOpen]=useState(false);
  const[sent,setSent]=useState(null); // motif envoyé (objet MOTIFS) ou null
  const joignables=(gerants||[]).filter(g=>!g.archived&&!g.endDate&&g.phone);
  const send=(motif)=>{
    // Notif Direction en FRANÇAIS (c'est la Direction qui la lit).
    try{
      notifEventBus.emit('assistance_demandee',{
        fromName:me?.name||'Salarié(e)',
        motif:motif.fr,
        summary:`🆘 ${me?.name||'Un salarié'} a besoin d'aide — ${motif.fr}`,
      });
    }catch(_){/* jamais bloquant */}
    // Trace PERSISTANTE (bandeau rouge + journal côté Direction) : le toast/push
    // sont éphémères ; ceci garantit qu'une demande d'aide ne se perd jamais.
    try{
      onAssist&&onAssist({
        id:'as'+Date.now(),
        sid:me?.id||null,
        fromName:me?.name||'Salarié(e)',
        motif:motif.fr,
        ts:new Date().toISOString(),
        traite:false,traitePar:null,traiteTs:null,
      });
    }catch(_){/* jamais bloquant */}
    setSent(motif);
  };
  const close=()=>{setOpen(false);setSent(null);};
  return(<div style={{marginBottom:16}}>
    <button onClick={()=>{setSent(null);setOpen(true);}} className="btn bor" style={{width:'100%',padding:'14px',fontSize:15,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:8,boxShadow:'0 3px 12px rgba(255,134,23,.28)'}}>
      🆘 {tr(langue,{fr:"J'ai un problème, appelez-moi",es:'Tengo un problema, llámenme'})}
    </button>
    {open&&<div className="mbk" onClick={close}>
      <div style={{background:'var(--bg)',borderRadius:16,padding:22,maxWidth:360,width:'100%',margin:'0 14px'}} onClick={e=>e.stopPropagation()}>
        {!sent?<>
          <div className="D" style={{fontSize:19,marginBottom:4}}>{tr(langue,{fr:'Que se passe-t-il ?',es:'¿Qué pasa?'})}</div>
          <div style={{fontSize:12,color:'var(--si)',marginBottom:14}}>{tr(langue,{fr:'Choisis, la Direction est prévenue tout de suite.',es:'Elige, la dirección será avisada enseguida.'})}</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {MOTIFS.map(motif=>(<button key={motif.id} onClick={()=>send(motif)} className="btn bgh" style={{textAlign:'left',padding:'12px 14px',fontSize:14,lineHeight:1.3}}>
              <div style={{fontWeight:700}}>{tr(langue,motif)}</div>
              {langue!=='fr'&&<div style={{fontSize:10,color:'var(--si)',marginTop:2}}>{motif.fr}</div>}
            </button>))}
          </div>
          <button onClick={close} className="btn bgh sm" style={{marginTop:14,width:'100%'}}>{tr(langue,{fr:'Annuler',es:'Cancelar'})}</button>
        </>:<>
          <div style={{fontSize:40,textAlign:'center',marginBottom:8}}>✅</div>
          <div className="D" style={{fontSize:19,textAlign:'center',color:'var(--ok)',marginBottom:4}}>{tr(langue,{fr:'La Direction est prévenue',es:'La dirección ha sido avisada'})}</div>
          <div style={{fontSize:12,color:'var(--si)',textAlign:'center',marginBottom:16}}>{tr(langue,{fr:'Tu peux aussi appeler directement :',es:'También puedes llamar directamente:'})}</div>
          {joignables.length>0?<div style={{display:'flex',flexDirection:'column',gap:8}}>
            {joignables.map(g=>(<a key={g.id} href={`tel:${g.phone}`} className="btn bmn" style={{textDecoration:'none',padding:'13px',fontSize:15,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
              📞 {g.name}
            </a>))}
          </div>:<div style={{fontSize:12,color:'var(--si)',textAlign:'center',fontStyle:'italic'}}>{tr(langue,{fr:'Aucun numéro enregistré — attends leur appel.',es:'Ningún número guardado — espera su llamada.'})}</div>}
          <button onClick={close} className="btn bgh" style={{marginTop:16,width:'100%'}}>{tr(langue,{fr:'Fermer',es:'Cerrar'})}</button>
        </>}
      </div>
    </div>}
  </div>);
}

export { AssistanceButton };

// ─── Lot C : clôture de journée attestée ───────────────────────────────────

// Code de clôture DÉTERMINISTE (date + salarié). But : la Direction peut vérifier
// que le code transmis par le salarié est le vrai code du jour (non inventable),
// et le recalculer même si la notif n'est pas arrivée. 4 chiffres.
export const clotureCode=(sid,dateStr)=>{
  const s=`${dateStr||''}|${sid||''}`;
  let h=0;
  for(let i=0;i<s.length;i++){h=(h*31+s.charCodeAt(i))>>>0;}
  return String(h%10000).padStart(4,'0');
};

// Écran de clôture de journée (encadrement renforcé). Deux modes :
//  - 'blocked' : check-list bilingue des tâches non terminées, la fin de poste
//    est refusée tant que ce n'est pas réglé (attrape « pointé le début, pas la fin »).
//  - 'receipt' : reçu bilingue avec le code à transmettre à la Direction.
// Purement présentational : l'écriture du pointage et la notif sont gérées par
// DailyPunch (l'appelant).
function ClotureJournee({mode,langue,blockers,code,onClose}){
  const L=(fr,es)=>tr(langue,{fr,es});
  const reasonLbl=r=>r==='rapport'?L('rapport à finir','informe por terminar'):L('appartement pas terminé','apartamento sin terminar');
  return(<div className="mbk" onClick={onClose}>
    <div style={{background:'var(--bg)',borderRadius:16,padding:22,maxWidth:380,width:'100%',margin:'0 14px'}} onClick={e=>e.stopPropagation()}>
      {mode==='blocked'?<>
        <div style={{fontSize:38,textAlign:'center',marginBottom:8}}>✋</div>
        <div className="D" style={{fontSize:19,textAlign:'center',color:'var(--dg)',marginBottom:4}}>{L('Tu ne peux pas encore finir ta journée','Aún no puedes terminar tu jornada')}</div>
        <div style={{fontSize:12,color:'var(--si)',textAlign:'center',marginBottom:14}}>{L('Il te reste à terminer :','Te queda por terminar:')}</div>
        <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:14}}>
          {(blockers||[]).map(b=>(<div key={b.id} style={{background:'#fde0e0',border:'1.5px solid var(--dg)',borderRadius:10,padding:'10px 12px'}}>
            <div style={{fontWeight:700,fontSize:14}}>{b.bienName}</div>
            <div style={{fontSize:11,color:'#8a0000'}}>{reasonLbl(b.reason)}</div>
          </div>))}
        </div>
        <div style={{fontSize:11,color:'var(--si)',textAlign:'center',marginBottom:12}}>{L('Si tu es bloquée, utilise le bouton 🆘 en haut.','Si estás bloqueada, usa el botón 🆘 de arriba.')}</div>
        <button onClick={onClose} className="btn bgh" style={{width:'100%'}}>{L('J\'ai compris','Entendido')}</button>
      </>:<>
        <div style={{fontSize:42,textAlign:'center',marginBottom:8}}>✅</div>
        <div className="D" style={{fontSize:20,textAlign:'center',color:'var(--ok)',marginBottom:4}}>{L('Journée clôturée','Jornada cerrada')}</div>
        <div style={{fontSize:12,color:'var(--si)',textAlign:'center',marginBottom:14}}>{L('Envoie ce code à la Direction :','Envía este código a la dirección:')}</div>
        <div style={{background:'var(--cr)',borderRadius:14,padding:'16px',textAlign:'center',marginBottom:12}}>
          <div className="D" style={{fontSize:38,letterSpacing:'.12em',color:'var(--or)'}}>{code}</div>
        </div>
        <div style={{fontSize:11,color:'var(--si)',textAlign:'center',marginBottom:16}}>{L('La Direction est aussi prévenue automatiquement.','La dirección también ha sido avisada automáticamente.')}</div>
        <button onClick={onClose} className="btn bok" style={{width:'100%'}}>{L('Fermer','Cerrar')}</button>
      </>}
    </div>
  </div>);
}

export { ClotureJournee };

// ─── Lot D : détection de trou entre appartements → pause oubliée / anomalie ──

// Seuils regroupés en un seul endroit (source unique). Défauts validés terrain ;
// à câbler dans un panneau de réglages admin si un ajustement s'avère nécessaire.
export const ER_CONFIG={
  sameAddrM:50,    // rayon GPS (m) en-deçà duquel deux biens sont « à la même adresse »
  sameAddrMin:10,  // transition attendue entre deux biens à la même adresse
  speedKmh:25,     // vitesse moyenne pour estimer le trajet (Colmar et environs)
  bufferMin:5,     // marge ajoutée au trajet estimé entre adresses différentes
  fallbackMin:20,  // transition par défaut si coordonnées GPS indisponibles
  pauseSeuilMin:15,// excédent minimal (min) au-delà duquel on suspecte une pause non pointée
};

// Temps de transition attendu (min) entre deux biens : 10 min à la même adresse
// (proximité GPS), sinon trajet estimé (distance à vol d'oiseau ÷ vitesse) + marge.
// Repli sur fallbackMin si un bien n'a pas de coordonnées.
export const expectedTransitionMin=(a,b,cfg=ER_CONFIG)=>{
  const has=a&&b&&a.lat!=null&&a.lng!=null&&b.lat!=null&&b.lng!=null;
  if(!has)return cfg.fallbackMin;
  const d=distM(a.lat,a.lng,b.lat,b.lng); // mètres
  if(d<=cfg.sameAddrM)return cfg.sameAddrMin;
  return Math.ceil((d/1000)/cfg.speedKmh*60)+cfg.bufferMin;
};

// Détecte un trou anormal avant le début d'un nouvel appart. Compare le trou réel
// (fin de l'appart précédent → début du nouveau) à la transition attendue. Renvoie
// les données de la modale si l'excédent atteint le seuil, sinon null (rien à
// signaler, ou calcul impossible : pas d'appart terminé avant, coords absentes gérées).
// `breaks` (v130) : tableau tc.breaks du jour, déjà à jour de la fermeture automatique.
export const detectPauseOubliee=({als,sid,todayS,newAl,biens,breaks=[],cfg=ER_CONFIG})=>{
  const newStart=new Date(newAl.ts).getTime();
  const ends=(als||[]).filter(l=>l.sid===sid&&l.type==='end'&&new Date(l.ts).getTime()<=newStart);
  if(!ends.length)return null; // aucun appart terminé avant → pas de transition à mesurer
  const prev=ends.reduce((m,l)=>new Date(l.ts).getTime()>new Date(m.ts).getTime()?l:m);
  const prevEnd=new Date(prev.ts).getTime();
  const gapMin=(newStart-prevEnd)/60000;
  const prevBien=biens.find(b=>b.id===prev.bid);
  const newBien=biens.find(b=>b.id===newAl.bid);
  const expected=expectedTransitionMin(prevBien,newBien,cfg);
  // Pauses DEJA declarees dans le trou (dont celle que le Debut vient de refermer
  // automatiquement) : on ne redemande jamais une pause que la salariee a pointee.
  const dejaMin=pausesDansFenetre(breaks,prevEnd,newStart);
  const excess=Math.round(gapMin-expected-dejaMin);
  if(excess<cfg.pauseSeuilMin)return null;
  return{alId:newAl.id,prevBienName:prevBien?.name||'?',newBienName:newBien?.name||'?',prevEnd,newStart,gapMin:Math.round(gapMin),expected,dejaMin:Math.round(dejaMin),excess};
};

// Modale bilingue « pause oubliée ? ». Non-fermable au clic extérieur : le salarié
// DOIT répondre (Oui → l'appli enregistre la pause suggérée ; Non → anomalie).
function PauseOubliee({langue,data,onOui,onNon}){
  const L=(fr,es)=>tr(langue,{fr,es});
  return(<div className="mbk">
    <div style={{background:'var(--bg)',borderRadius:16,padding:22,maxWidth:380,width:'100%',margin:'0 14px'}}>
      <div style={{fontSize:38,textAlign:'center',marginBottom:8}}>☕</div>
      <div className="D" style={{fontSize:19,textAlign:'center',marginBottom:6}}>{L('Tu as fait une pause ?','¿Hiciste una pausa?')}</div>
      <div style={{fontSize:12,color:'var(--si)',textAlign:'center',marginBottom:14,lineHeight:1.5}}>
        {L(`Entre ${data.prevBienName} et ${data.newBienName}, il s'est passé ${data.gapMin} min. Le trajet en prend environ ${data.expected}.`,`Entre ${data.prevBienName} y ${data.newBienName} pasaron ${data.gapMin} min. El trayecto lleva unos ${data.expected}.`)}
      </div>
      <div style={{background:'var(--cr)',borderRadius:12,padding:'12px',textAlign:'center',marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700}}>{L(`Pause d'environ ${data.excess} min ?`,`¿Pausa de unos ${data.excess} min?`)}</div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        <button onClick={onOui} className="btn bok" style={{padding:'13px',fontSize:15,fontWeight:700}}>{L(`Oui, j'ai fait une pause d'environ ${data.excess} min`,`Sí, hice una pausa de unos ${data.excess} min`)}</button>
        <button onClick={onNon} className="btn bgh" style={{padding:'13px',fontSize:14}}>{L('Non, pas de pause','No, sin pausa')}</button>
      </div>
      <div style={{fontSize:10,color:'var(--si)',textAlign:'center',marginTop:10}}>{L('La Direction peut ajuster ensuite si besoin.','La dirección puede ajustar después si hace falta.')}</div>
    </div>
  </div>);
}

export { PauseOubliee };
